import { Observable, of } from 'rxjs';

import {
	AuthService,
	Container,
	history,
	Service,
} from '../../../../symphony';

import { SlActions } from '../ActionsService/sl-actions';
import {
	ActionType,
	MainCTAType,
	PaginationCTAType,
	RequestType,
	TrackActionName,
} from '../../global-interface';
import { SlCodeBitsContext } from '../../global-constants';
import { SlDataService } from '../sl-data.service';

@Service()
export class SlEventsService {
	private actions$ = Container.take(SlCodeBitsContext, SlActions).actions$;

	private dataService = Container.take(SlCodeBitsContext, SlDataService);

	private authService = Container.take('global', AuthService);

	public onCodeBitsListPageMount = (): Observable<null> => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCodeBitsPageMount,
				},
			},
		});

		return of(null);
	};

	public onHotTodayMount = () => {
		this.actions$.next({
			type: ActionType.GetHotTodayTopics,
			requestType: RequestType.Parallel,
		});

		return of(null);
	};

	public onProfileButtonClick = (userId: number) => {
		history.push(`/profile/${userId}`);
		return of(null);
	};

	public onProjectClick = (codeId: string) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnProjectClick,
					source: 'DEFAULT',
					codeId,
				},
			},
		});
		return of(null);
	};

	public onTopicClick = (codeId: string) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnProjectClick,
					source: 'HOT TOPIC',
					codeId,
				},
			},
		});
		return of(null);
	};

	public onSearchClick = (searchedInputValue: string) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnSearchClick,
					searchText: searchedInputValue,
				},
			},
		});

		return of(null);
	};

	public onNewCodeClick = () => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCTAClick,
					ctaType: MainCTAType.NewCode,
				},
			},
		});

		if (!this.authService.isLoggedIn()) {
			history.push('/users/signup?returnUrl=/compiler-playground');
			return of(null);
		}

		history.push('/compiler-playground');

		return of(null);
	};

	public onPaginationClick = (
		currentPageNumber: number,
		paginationCtaType: PaginationCTAType,
		clickedPageNumber: number,
	) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnPaginationClick,
					paginationCtaType,
					pageNumber: currentPageNumber,
					clickPageNumber: clickedPageNumber,
				},
			},
		});

		return of(null);
	};

	public onEmptyCodeBitsCTAClick = (
		ctaType: MainCTAType,
		pathname: string,
	) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCTAClick,
					ctaType,
				},
			},
		});

		if (ctaType === MainCTAType.SignUp) {
			history.push(
				`/users/signup?returnUrl=${encodeURIComponent(`${pathname}`)}`,
			);
			return of(null);
		}

		if (ctaType === MainCTAType.SignIn) {
			history.push(
				`/users/login?returnUrl=${encodeURIComponent(`${pathname}`)}`,
			);
			return of(null);
		}

		if (
			ctaType === MainCTAType.NewCode
            || ctaType === MainCTAType.CreateNewCode
		) {
			history.push(
				`/compiler-playground?returnUrl=${encodeURIComponent(
					`${pathname}`,
				)}`,
			);
			return of(null);
		}

		return of(null);
	};

	private defineTrackingLanguage = (language: string) => {
		if (!language || language === 'all') {
			return 'ALL LANGUAGES';
		}
		return this.dataService.supportedLanguages$.value?.find(
			(l) => l.key === language,
		)?.name;
	};

	public orderFilterChange = (
		selectedOrderFilter: string,
		selectedLanguageFilter: string,
	) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OrderFilterChange,
					sectionName: selectedOrderFilter,
					languageName: this.defineTrackingLanguage(
						selectedLanguageFilter,
					),
				},
			},
		});

		return of(null);
	};

	public languageFilterChange = (
		selectedLanguageFilter: string,
		selectedOrderFilter: string,
	) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OrderFilterChange,
					languageName: this.defineTrackingLanguage(
						selectedLanguageFilter,
					),
					sectionName: selectedOrderFilter,
				},
			},
		});

		return of(null);
	};

	public onCodeBitsListMount = (
		codeBitsListSearchText: string,
		orderFilter: string,
		currentPageNumber: number,
		languageFilter: string,
	) => {
		this.actions$.next({
			type: ActionType.GetCodeBitsList,
			requestType: RequestType.Parallel,
			data: {
				codeBitsListSearchText,
				orderFilter,
				currentPageNumber,
				languageFilter,
			},
		});

		this.actions$.next({
			type: ActionType.GetLanguagesCatalog,
			requestType: RequestType.Parallel,
		});

		return of(null);
	};
}
